/**
 * OrbitalLoadingView
 *
 * Orbital 기반 인터랙티브 로딩 화면
 * - SVG 기반 궤도 시스템
 * - 궤도별 회전 애니메이션 (교차 방향)
 * - 단계별 데이터 표시 (THREADS → FRIENDS → AVATAR)
 * - 반응형 설정
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { OrbitalRing, OrbitalItem, OrbitalCenter } from './orbital';
import {
  useOrbitalConfig,
  calculateOrbitalPositions,
  getStartAngle,
  getRotationDirection,
  truncateText,
} from '../../hooks/useOrbitalConfig';

/**
 * 데이터를 궤도에 분배
 *
 * @param {Array} items - 분배할 아이템 배열
 * @param {Array} orbits - 궤도 설정 배열
 * @returns {Array} 궤도별 아이템 배열
 */
const distributeItemsToOrbits = (items, orbits) => {
  if (!items || items.length === 0 || !orbits) return [];

  const result = [];
  let itemIndex = 0;

  orbits.forEach((orbit) => {
    const orbitItems = [];
    for (let i = 0; i < orbit.items && itemIndex < items.length; i++) {
      orbitItems.push(items[itemIndex]);
      itemIndex++;
    }
    result.push(orbitItems);
  });

  return result;
};

/**
 * OrbitalLoadingView
 *
 * @param {object} loadingState - 로딩 상태 객체
 * @param {number} year - 연도
 */
const OrbitalLoadingView = ({ loadingState, year = 2025 }) => {
  const { t } = useTranslation();
  const config = useOrbitalConfig();

  const {
    stage,
    sampleTexts,
    totalThreads,
    dateRange,
    friendsAnalyzing,
  } = loadingState || {};

  const isThreadsStage = stage?.id === 'threads';
  const isFriendsStage = stage?.id === 'friends';
  const isAvatarStage = stage?.id === 'friends_avatar';

  // 날짜 범위 포맷
  const formatDateRange = () => {
    if (!dateRange) return '';
    const startParts = dateRange.start.split('-');
    const endParts = dateRange.end.split('-');
    return `${startParts[0]}년 ${parseInt(startParts[1])}월 ~ ${parseInt(endParts[1])}월`;
  };

  // 중앙 메시지 결정
  const centerMessage = useMemo(() => {
    if (isThreadsStage) return t('yearEnd.loading.analyzingThreads');
    if (isFriendsStage) return t('yearEnd.loading.analyzingFriends');
    if (isAvatarStage) return t('yearEnd.loading.loadingAvatars');
    return t('yearEnd.loading.preparing');
  }, [isThreadsStage, isFriendsStage, isAvatarStage, t]);

  // 부가 메시지 결정
  const subMessage = useMemo(() => {
    if (isThreadsStage) {
      const parts = [];
      if (dateRange) parts.push(formatDateRange());
      if (totalThreads > 0) parts.push(t('yearEnd.loading.threadsFound', { count: totalThreads }));
      return parts.join(' | ') || null;
    }
    if (isFriendsStage && friendsAnalyzing?.length > 0) {
      return `${friendsAnalyzing.length}명의 친구 분석 중...`;
    }
    if (isAvatarStage && friendsAnalyzing?.length > 0) {
      return `${friendsAnalyzing.length}명의 프로필 이미지 불러오는 중...`;
    }
    return null;
  }, [isThreadsStage, isFriendsStage, isAvatarStage, dateRange, totalThreads, friendsAnalyzing, t]);

  // 스레드 텍스트 -> 궤도별 분배
  const threadItems = useMemo(() => {
    if (!isThreadsStage || !sampleTexts || sampleTexts.length === 0) return [];
    const items = sampleTexts.map((text, idx) => ({
      id: `thread-${idx}`,
      text: truncateText(text, 25),
      type: 'thread',
    }));
    return distributeItemsToOrbits(items, config.orbits);
  }, [isThreadsStage, sampleTexts, config.orbits]);

  // 친구 -> 궤도별 분배
  const friendItems = useMemo(() => {
    if (!isFriendsStage || !friendsAnalyzing || friendsAnalyzing.length === 0) return [];
    const items = friendsAnalyzing.map((friend, idx) => ({
      id: `friend-${idx}`,
      text: friend.username,
      type: 'friend',
    }));
    return distributeItemsToOrbits(items, config.orbits);
  }, [isFriendsStage, friendsAnalyzing, config.orbits]);

  // 현재 표시할 아이템
  const currentItems = isThreadsStage ? threadItems : isFriendsStage ? friendItems : [];

  // ViewBox 문자열
  const viewBoxStr = `0 0 ${config.viewBox.width} ${config.viewBox.height}`;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        py: { xs: 3, md: 5 },
        px: 2,
      }}
    >
      {/* 연말정산 타이틀 (상단) */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 20,
          textAlign: 'center',
          mb: { xs: 2, md: 3 },
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            color: 'primary.main',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
          }}
        >
          {t('yearEnd.loading.title', { year })}
        </Typography>
      </Box>

      {/* Orbital SVG 영역 */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: config.viewBox.width,
          aspectRatio: `${config.viewBox.width} / ${config.viewBox.height}`,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={stage?.id || 'default'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%', height: '100%' }}
          >
            <svg
              viewBox={viewBoxStr}
              style={{
                width: '100%',
                height: '100%',
                overflow: 'visible',
              }}
            >
              {/* 궤도 링 (점선 원) */}
              {config.orbits.map((orbit, orbitIndex) => (
                <OrbitalRing
                  key={`ring-${orbitIndex}`}
                  radius={orbit.radius}
                  center={config.center}
                  strokeColor="#333"
                  opacity={0.2}
                  animationDelay={orbitIndex * 0.15}
                />
              ))}

              {/* 회전하는 아이템 그룹 */}
              {currentItems.map((orbitItems, orbitIndex) => {
                if (!orbitItems || orbitItems.length === 0) return null;

                const orbit = config.orbits[orbitIndex];
                const direction = getRotationDirection(orbitIndex);
                const startAngle = getStartAngle(orbitIndex);
                const positions = calculateOrbitalPositions(
                  orbitItems.length,
                  orbit.radius,
                  startAngle,
                  config.center
                );

                return (
                  <motion.g
                    key={`orbit-group-${orbitIndex}`}
                    style={{
                      transformOrigin: `${config.center.x}px ${config.center.y}px`,
                    }}
                    animate={{
                      rotate: direction === 'cw' ? [0, 360] : [0, -360],
                    }}
                    transition={{
                      duration: orbit.rotationDuration,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  >
                    {orbitItems.map((item, itemIndex) => {
                      const pos = positions[itemIndex];
                      if (!pos) return null;

                      return (
                        <motion.g
                          key={item.id}
                          style={{
                            transformOrigin: `${pos.x}px ${pos.y}px`,
                          }}
                          animate={{
                            // 텍스트가 항상 읽을 수 있도록 역회전
                            rotate: direction === 'cw' ? [0, -360] : [0, 360],
                          }}
                          transition={{
                            duration: orbit.rotationDuration,
                            repeat: Infinity,
                            ease: 'linear',
                          }}
                        >
                          <OrbitalItem
                            x={pos.x}
                            y={pos.y}
                            angle={pos.angle}
                            text={item.text}
                            type={item.type}
                            fontSize={orbit.fontSize}
                            markerSize={config.markerSize}
                            markerColor={item.type === 'friend' ? '#1976d2' : '#666'}
                            textColor={item.type === 'friend' ? '#1976d2' : '#333'}
                            animationDelay={orbitIndex * 0.3 + itemIndex * 0.15}
                          />
                        </motion.g>
                      );
                    })}
                  </motion.g>
                );
              })}
            </svg>

            {/* 중앙 로딩 메시지 (HTML overlay) */}
            <OrbitalCenter
              stage={stage?.id}
              message={centerMessage}
              subMessage={subMessage}
              size={config.centerSize}
              showAvatarPlaceholders={isAvatarStage}
            />
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* 단계 인디케이터 (하단) */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mt: { xs: 2, md: 3 },
          zIndex: 20,
        }}
      >
        {['threads', 'friends', 'friends_avatar'].map((stageId, idx) => (
          <Box
            key={stageId}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: stage?.id === stageId ? 'primary.main' : 'grey.300',
              transition: 'background-color 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default OrbitalLoadingView;
